import { useSortedSectionsForSectionGroupKey } from '#~/room/components/image/settings/hooks.js';
import type { HtmlAttributes } from '@studio/design-system/props';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { twMerge } from 'cn';
import { shallow } from 'zustand/shallow';
import { useStore } from '../store/hooks.js';
// Imported from types.js, not the store barrel: the barrel also exports the
// Provider, which drags the whole device layer into this layout leaf.
import { Panel } from '../store/types.js';
import { getCurrentSectionForPanel } from '../utils/section-state-utils.js';
import { Sections } from './sections.js';

export type PanelMap = {
  [K in (typeof Panel)[keyof typeof Panel]]: keyof typeof Panel;
};

export const PANEL_MAP: PanelMap = Object.fromEntries(
  Object.entries(Panel).map(([key, value]) => [value, key])
) as PanelMap;

/**
 * Props for the ScrollableSections component
 */
export interface ScrollableSectionsProps extends HtmlAttributes {
  /** Callback when section changes */
  onSectionChange?: (section: string) => void;

  /**
   * Registry section group whose sub-sections this strip lists. Supply it
   * whenever the panel and the section group differ — deriving the group from
   * PANEL_MAP works only while the Panel enum KEY equals the section group
   * name, which the predefined Control tab (Panel.Control, group 'Controls')
   * breaks. Omitted, the strip falls back to PANEL_MAP.
   */
  sectionGroupKey?: string;
}

/**
 * Component for displaying horizontally scrollable sections with fade-out effect
 */
export const ScrollableSections = ({
  className,
  onSectionChange,
  sectionGroupKey,
  ...props
}: ScrollableSectionsProps): ReactElement => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [candidate, panel] = useStore((µ) => [µ.candidate, µ.panel], shallow);
  const storeState = useStore((µ) => µ, shallow);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(true);
  const rafRef = useRef<number | null>(null);

  // Get sorted sections for the current panel
  const sortedSections = useSortedSectionsForSectionGroupKey(
    candidate.id,
    sectionGroupKey ?? PANEL_MAP[panel]
  );

  // Get current section based on panel using dynamic utility
  const currentSection = getCurrentSectionForPanel(storeState, panel);

  // Update fade visibility based on scroll position
  const updateFadeVisibility = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollLeft = container.scrollLeft;
    const maxScroll = container.scrollWidth - container.clientWidth;
    const threshold = 5;

    // Show left fade only if scrolled to the right beyond threshold
    const shouldShowLeft = scrollLeft > threshold;

    // Show right fade only if there's more content to scroll to
    const shouldShowRight = scrollLeft < maxScroll - threshold;

    setShowLeftFade(shouldShowLeft);
    setShowRightFade(shouldShowRight);
  }, []);

  // Throttled scroll handler using requestAnimationFrame
  const handleScroll = useCallback(() => {
    if (rafRef.current) return;

    rafRef.current = requestAnimationFrame(() => {
      updateFadeVisibility();
      rafRef.current = null;
    });
  }, [updateFadeVisibility]);

  // Handle wheel events for mouse scroll support
  const handleWheel = useCallback(
    (e: WheelEvent) => {
      const container = scrollContainerRef.current;
      if (!container) return;

      const hasHorizontalScroll = container.scrollWidth > container.clientWidth;
      if (!hasHorizontalScroll) return;

      // Convert vertical wheel to horizontal scroll if no horizontal wheel movement
      if (Math.abs(e.deltaX) < Math.abs(e.deltaY)) {
        e.preventDefault();
        container.scrollLeft += e.deltaY;
        // Manually trigger our scroll handler since wheel events might not trigger scroll events
        handleScroll();
      }
    },
    [handleScroll]
  );

  // Handle touch events for mobile device support
  const handleTouchMove = useCallback(() => {
    // Touch scrolling naturally triggers scroll events, but we ensure our handler runs
    handleScroll();
  }, [handleScroll]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const container = scrollContainerRef.current;
      if (!container) return;

      const hasHorizontalScroll = container.scrollWidth > container.clientWidth;
      if (!hasHorizontalScroll) return;

      const scrollAmount = 100; // Pixels to scroll per key press

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        container.scrollLeft -= scrollAmount;
        handleScroll();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        container.scrollLeft += scrollAmount;
        handleScroll();
      }
    },
    [handleScroll]
  );

  // Set up scroll, wheel, touch, and keyboard event listeners
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const abortController = new AbortController();
    const { signal } = abortController;

    // Make container focusable for keyboard navigation
    container.setAttribute('tabindex', '0');

    // Initial check
    updateFadeVisibility();

    container.addEventListener('scroll', handleScroll, { passive: true, signal });
    container.addEventListener('wheel', handleWheel, { passive: false, signal });
    container.addEventListener('touchmove', handleTouchMove, { passive: true, signal });
    container.addEventListener('keydown', handleKeyDown, { passive: false, signal });

    return () => {
      abortController.abort();
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [handleScroll, handleWheel, handleTouchMove, handleKeyDown, updateFadeVisibility]);

  return (
    <div className={twMerge('flex w-full flex-col', className)} {...props}>
      <div className='relative w-full'>
        {/* Scrollable container with text-only fade-out effect */}
        <div className='relative'>
          {/* Scrollable container with mask-based fade effects */}
          <div
            ref={scrollContainerRef}
            className={twMerge(
              'scrollbar-hide flex w-full overflow-x-auto',
              // Apply mask-based fade effects based on scroll state
              showLeftFade && showRightFade && 'mask-fade-both',
              showLeftFade && !showRightFade && 'mask-fade-left',
              !showLeftFade && showRightFade && 'mask-fade-right'
            )}
          >
            <Sections
              sections={sortedSections}
              currentSection={currentSection}
              onSectionChange={onSectionChange || (() => {})}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
